(() => {
  const FORM_SELECTOR = '.f24-header-preview__search, .f24-hero-v3-search';
  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 180;
  const cache = new Map();
  let panelCounter = 0;

  const shopRoot = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const searchUrl = (query) => `${shopRoot()}search?q=${encodeURIComponent(query)}`;

  const createText = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    node.textContent = text;
    return node;
  };

  class F24LiveSearch {
    constructor(form) {
      this.form = form;
      this.input = form.querySelector('input[type="search"][name="q"]');
      if (!this.input || form.closest('predictive-search')) return;

      this.controller = null;
      this.timer = null;
      this.activeIndex = -1;
      this.items = [];
      this.lastQuery = '';
      this.raf = 0;
      this.id = `F24LiveSearch-${++panelCounter}`;

      this.panel = document.createElement('div');
      this.panel.id = this.id;
      this.panel.className = 'f24-live-search-panel';
      this.panel.setAttribute('role', 'listbox');
      this.panel.setAttribute('aria-label', 'Suchvorschläge');
      document.body.appendChild(this.panel);

      this.input.setAttribute('autocomplete', 'off');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-controls', this.id);
      this.input.setAttribute('aria-expanded', 'false');

      this.bindEvents();
    }

    bindEvents() {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('focus', () => this.onFocus());
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.form.addEventListener('submit', () => this.close());

      document.addEventListener('pointerdown', (event) => {
        if (!this.form.contains(event.target) && !this.panel.contains(event.target)) this.close();
      });

      window.addEventListener('resize', () => this.schedulePosition());
      window.addEventListener('scroll', () => this.schedulePosition(), true);
    }

    onInput() {
      const query = this.input.value.trim();
      this.lastQuery = query;
      this.activeIndex = -1;
      window.clearTimeout(this.timer);

      if (query.length < MIN_QUERY_LENGTH) {
        this.abort();
        this.close();
        return;
      }

      const key = query.toLocaleLowerCase();
      if (cache.has(key)) {
        this.render(query, cache.get(key));
        return;
      }

      this.timer = window.setTimeout(() => this.fetchResults(query), DEBOUNCE_MS);
    }

    onFocus() {
      const query = this.input.value.trim();
      if (query.length < MIN_QUERY_LENGTH) return;
      const cached = cache.get(query.toLocaleLowerCase());
      if (cached) this.render(query, cached);
    }

    onKeydown(event) {
      if (!this.isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveActive(1);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveActive(-1);
        return;
      }

      if (event.key === 'Enter' && this.activeIndex >= 0 && this.items[this.activeIndex]) {
        event.preventDefault();
        window.location.href = this.items[this.activeIndex].href;
      }
    }

    moveActive(step) {
      if (!this.items.length) return;
      this.activeIndex = (this.activeIndex + step + this.items.length) % this.items.length;
      this.items.forEach((item, index) => {
        const active = index === this.activeIndex;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      const activeItem = this.items[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', activeItem.id);
      activeItem.scrollIntoView({ block: 'nearest' });
    }

    abort() {
      if (this.controller) {
        this.controller.abort();
        this.controller = null;
      }
    }

    async fetchResults(query) {
      this.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product,collection,query');
      params.set('resources[limit]', '6');
      params.set('resources[limit_scope]', 'each');
      params.set('resources[options][unavailable_products]', 'last');

      try {
        const response = await fetch(`${shopRoot()}search/suggest.json?${params.toString()}`, {
          headers: { Accept: 'application/json' },
          signal: this.controller.signal,
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
        const json = await response.json();
        const results = json && json.resources && json.resources.results ? json.resources.results : {};
        cache.set(query.toLocaleLowerCase(), results);
        if (this.input.value.trim() === query) this.render(query, results);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        this.controller = null;
      }
    }

    render(query, results) {
      this.panel.replaceChildren();
      this.activeIndex = -1;
      this.items = [];
      this.input.removeAttribute('aria-activedescendant');

      const products = Array.isArray(results.products) ? results.products : [];
      const collections = Array.isArray(results.collections) ? results.collections : [];
      const queries = Array.isArray(results.queries) ? results.queries : [];

      if (products.length) this.addSection('Produkte', products, (item) => this.productItem(item));
      if (collections.length) this.addSection('Kategorien', collections, (item) => this.simpleItem(item.title, item.url, 'Kategorie'));
      if (queries.length) {
        this.addSection('Suchvorschläge', queries, (item) => {
          const text = item.text || item.styled_text || '';
          return this.simpleItem(text, item.url || searchUrl(text), 'Suche');
        });
      }

      if (!products.length && !collections.length && !queries.length) {
        this.panel.appendChild(createText('div', 'f24-live-search__empty', 'Keine direkten Treffer.'));
      }

      const all = document.createElement('a');
      all.className = 'f24-live-search__all';
      all.href = searchUrl(query);
      all.textContent = `Alle Ergebnisse für „${query}“`;
      this.panel.appendChild(all);

      this.items = Array.from(this.panel.querySelectorAll('[data-f24-search-item]'));
      this.items.forEach((item, index) => {
        item.id = `${this.id}-item-${index}`;
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.addEventListener('mouseenter', () => {
          this.activeIndex = index;
          this.items.forEach((entry, entryIndex) => entry.classList.toggle('is-active', entryIndex === index));
        });
      });

      this.open();
    }

    addSection(title, items, builder) {
      const section = document.createElement('div');
      section.className = 'f24-live-search__section';
      section.appendChild(createText('div', 'f24-live-search__heading', title));
      items.forEach((item) => {
        const node = builder(item);
        if (node) section.appendChild(node);
      });
      this.panel.appendChild(section);
    }

    productItem(product) {
      const link = document.createElement('a');
      link.className = 'f24-live-search__item';
      link.href = product.url || '#';
      link.dataset.f24SearchItem = 'true';

      const featured = product.featured_image;
      const imageUrl = featured && typeof featured === 'object' ? featured.url : featured;
      if (imageUrl) {
        const image = document.createElement('img');
        image.className = 'f24-live-search__thumb';
        image.src = imageUrl;
        image.alt = featured && typeof featured === 'object' && featured.alt_text ? featured.alt_text : '';
        image.loading = 'lazy';
        image.width = 52;
        image.height = 52;
        link.appendChild(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'f24-live-search__thumb f24-live-search__thumb--empty';
        placeholder.setAttribute('aria-hidden', 'true');
        link.appendChild(placeholder);
      }

      const copy = document.createElement('span');
      copy.className = 'f24-live-search__copy';
      copy.appendChild(createText('span', 'f24-live-search__title', product.title || 'Produkt'));
      if (product.type || product.vendor) {
        copy.appendChild(createText('span', 'f24-live-search__meta', product.type || product.vendor));
      }
      link.appendChild(copy);
      return link;
    }

    simpleItem(title, url, meta) {
      if (!title) return null;
      const link = document.createElement('a');
      link.className = 'f24-live-search__item f24-live-search__item--simple';
      link.href = url || searchUrl(title);
      link.dataset.f24SearchItem = 'true';
      const copy = document.createElement('span');
      copy.className = 'f24-live-search__copy';
      copy.appendChild(createText('span', 'f24-live-search__title', title));
      copy.appendChild(createText('span', 'f24-live-search__meta', meta));
      link.appendChild(copy);
      return link;
    }

    isOpen() {
      return this.panel.classList.contains('is-open');
    }

    open() {
      this.panel.classList.add('is-open');
      this.input.setAttribute('aria-expanded', 'true');
      this.schedulePosition();
    }

    close() {
      this.panel.classList.remove('is-open');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.items.forEach((item) => item.classList.remove('is-active'));
    }

    schedulePosition() {
      if (!this.isOpen()) return;
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.position());
    }

    position() {
      if (!this.isOpen()) return;
      const rect = this.form.getBoundingClientRect();
      const viewportPadding = 12;
      const width = Math.min(Math.max(rect.width, 320), window.innerWidth - viewportPadding * 2);
      const maxLeft = window.scrollX + window.innerWidth - width - viewportPadding;
      const left = Math.max(window.scrollX + viewportPadding, Math.min(window.scrollX + rect.left, maxLeft));
      this.panel.style.width = `${Math.max(0, width)}px`;
      this.panel.style.left = `${left}px`;
      this.panel.style.top = `${window.scrollY + rect.bottom + 8}px`;
    }
  }

  const init = () => {
    document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
      if (!form.dataset.f24LiveSearchReady) {
        form.dataset.f24LiveSearchReady = 'true';
        new F24LiveSearch(form);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
